import { useMemo, useRef, useState } from "react";
import { isActiveEmp } from "../lib/constants";
import { toast } from "sonner";
import { FolderUp, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api } from "../lib/api";
import { normSearch } from "../lib/searchText";

const OK_EXT = /\.(jpe?g|png|webp|heic|heif|gif|pdf)$/i;
const CAT_RULES = [
  ["najm_report", /نجم|مرور|najm/i], ["hospital_report", /مستشف|طبي|hospital|medical/i],
  ["injury_template", /كليش/i], ["operating_contract", /عقد|contract/i], ["attendance", /حضور|انصراف|attendance/i],
  ["order_at_accident", /طلب وقت|وقت الحادث/i], ["day_total_orders", /اجمالي|إجمالي|total/i],
  ["accident_photos", /حادث|accident/i], ["id_page", /معرف|معرّف/i],
];
const catOf = (name) => (CAT_RULES.find(([, re]) => re.test(name)) || ["admin_files"])[0];

const pad = (n) => String(n).padStart(2, "0");
const dateFrom = (name, files) => {
  let m = name.match(/(20\d{2})[-_./](\d{1,2})[-_./](\d{1,2})/);
  if (m) return `${m[1]}-${pad(m[2])}-${pad(m[3])}`;
  m = name.match(/(\d{1,2})[-_./](\d{1,2})[-_./](20\d{2})/);
  if (m) return `${m[3]}-${pad(m[2])}-${pad(m[1])}`;
  const t = Math.min(...files.map((f) => f.lastModified || Date.now()));
  return new Date(t).toISOString().slice(0, 10);
};

const matchEmp = (folder, employees) => {
  const digits = (folder.match(/\d{9,}/) || [])[0];
  if (digits) {
    const e = employees.find((x) => String(x.iqama_number || "").replace(/\D/g, "") === digits);
    if (e) return e.id;
  }
  const q = normSearch(folder.replace(/[\d_\-./]+/g, " "));
  if (q.length < 3) return "";
  const exact = employees.filter((x) => normSearch(x.name) === q);
  if (exact.length === 1) return exact[0].id;
  const part = employees.filter((x) => { const n = normSearch(x.name); return n && (n.includes(q) || q.includes(n)); });
  return part.length === 1 ? part[0].id : "";
};

const buildRows = (files, employees) => {
  const groups = {};
  files.forEach((f) => {
    const parts = (f.webkitRelativePath || f.name).split("/");
    const key = parts.length > 2 ? parts[1] : parts[0];
    (groups[key] = groups[key] || []).push(f);
  });
  return Object.entries(groups).map(([folder, list]) => {
    const ok = list.filter((f) => OK_EXT.test(f.name));
    return { folder, files: ok, skipped: list.length - ok.length, employee_id: matchEmp(folder, employees), date: dateFrom(folder, list), include: ok.length > 0 };
  });
};

const Row = ({ r, i, employees, upd }) => (
  <tr className={`border-b ${r.employee_id ? "" : "bg-amber-50"}`} data-testid={`inj-imp-row-${i}`}>
    <td className="p-1.5"><input type="checkbox" checked={r.include} disabled={!r.files.length} onChange={(e) => upd(i, { include: e.target.checked })} data-testid={`inj-imp-include-${i}`} /></td>
    <td className="max-w-[160px] truncate p-1.5 font-semibold" title={r.folder}>{r.folder}</td>
    <td className="p-1.5 text-center">{r.files.length}{r.skipped ? <span className="text-[10px] text-slate-400"> (+{r.skipped} غير مدعوم)</span> : null}</td>
    <td className="p-1.5">
      <select value={r.employee_id} onChange={(e) => upd(i, { employee_id: e.target.value })} data-testid={`inj-imp-emp-${i}`}
        className={`h-8 w-full min-w-[160px] rounded-md border bg-white px-1 text-xs ${r.employee_id ? "border-slate-200" : "border-amber-400"}`}>
        <option value="">— لم يتطابق، اختر المندوب —</option>
        {employees.filter(isActiveEmp).map((e) => <option key={e.id} value={e.id}>{e.name}{e.iqama_number ? ` · ${e.iqama_number}` : ""}</option>)}
      </select>
    </td>
    <td className="p-1.5"><Input type="date" value={r.date} onChange={(e) => upd(i, { date: e.target.value })} className="h-8 text-xs" data-testid={`inj-imp-date-${i}`} /></td>
  </tr>
);

export const InjuryFolderImport = ({ employees = [], onDone }) => {
  const ref = useRef(null);
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);
  const upd = (i, patch) => setRows((p) => p.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const ready = useMemo(() => (rows || []).filter((r) => r.include && r.employee_id && r.files.length), [rows]);
  const unmatched = (rows || []).filter((r) => r.include && !r.employee_id).length;

  const pick = (list) => {
    const files = Array.from(list || []);
    if (!files.length) return;
    setRows(buildRows(files, employees));
  };
  const run = async () => {
    const total = ready.reduce((n, r) => n + r.files.length, 0);
    let done = 0; let failed = 0;
    setBusy({ done, total });
    for (const r of ready) {
      try {
        const inj = await api.createInjury({ employee_id: r.employee_id, injury_date: r.date, notes: `مستورد من مجلد: ${r.folder}` });
        await api.updateInjury(inj.id, { status: "closed" });
        for (const f of r.files) {
          try { await api.uploadInjuryFile(inj.id, catOf(f.name), f); } catch { failed += 1; }
          done += 1; setBusy({ done, total });
        }
      } catch (e) { failed += r.files.length; toast.error(`${r.folder}: ${e?.response?.data?.detail || "تعذّر الإنشاء"}`); }
    }
    setBusy(null); setRows(null);
    toast.success(`تم استيراد ${ready.length} ملف إصابة (${done - failed} مرفق)${failed ? ` — تعذّر ${failed}` : ""}`);
    onDone?.();
  };

  return (
    <>
      <input ref={ref} type="file" hidden webkitdirectory="" directory="" multiple data-testid="inj-folder-input"
        onChange={(e) => { pick(e.target.files); e.target.value = ""; }} />
      <Button variant="outline" data-testid="inj-folder-import-btn" onClick={() => ref.current?.click()} className="h-9 gap-1.5 border-rose-200 text-rose-700">
        <FolderUp className="h-4 w-4" /> استيراد مجلد
      </Button>
      <Dialog open={!!rows} onOpenChange={(v) => { if (!v && !busy) setRows(null); }}>
        <DialogContent className="max-w-4xl w-[96vw] max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="inj-import-dialog">
          <DialogHeader className="text-right">
            <DialogTitle>معاينة استيراد إصابات العمل القديمة</DialogTitle>
            <DialogDescription>كل مجلد مندوب يصبح ملف إصابة «مغلق». الصفوف الصفراء لم يتطابق مندوبها، اختره يدوياً. الملفات تُصنَّف تلقائياً حسب اسمها (نجم، مستشفى، حادث…).</DialogDescription>
          </DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-right text-xs">
              <thead className="bg-slate-50 text-slate-500"><tr><th className="p-1.5" /><th className="p-1.5">المجلد</th><th className="p-1.5">الملفات</th><th className="p-1.5">المندوب</th><th className="p-1.5">تاريخ الإصابة</th></tr></thead>
              <tbody>{(rows || []).map((r, i) => <Row key={r.folder} r={r} i={i} employees={employees} upd={upd} />)}</tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            <span className="text-xs text-slate-500" data-testid="inj-import-summary">جاهز: {ready.length} مجلد{unmatched ? ` · ${unmatched} بدون مندوب (لن يُستورد)` : ""}</span>
            <Button data-testid="inj-import-confirm" disabled={!ready.length || !!busy} onClick={run} className="gap-1.5 bg-rose-600 hover:bg-rose-700">
              {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> جاري الرفع {busy.done}/{busy.total}</> : `استيراد ${ready.length} ملف إصابة`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
